export const WidgetType = {
  EVENT_CAROUSEL_WIDGET: 'EVENT_CAROUSEL_WIDGET',
  INVENTORY_MAP_WIDGET: 'INVENTORY_MAP_WIDGET',
  INVENTORY_LIST_WIDGET: 'INVENTORY_LIST_WIDGET',
  CART_WIDGET: 'CART_WIDGET',
  RESERVATION_FORM_WIDGET: 'RESERVATION_FORM_WIDGET',
  GUEST_LIST_FORM_WIDGET: 'GUEST_LIST_FORM_WIDGET',
  EVENT_LIST_WIDGET: 'EVENT_LIST_WIDGET',
  EVENT_CALENDAR_WIDGET: 'EVENT_CALENDAR_WIDGET',
  EVENT_GRID_WIDGET: 'EVENT_GRID_WIDGET',
} as const

export type WidgetTypeValue = (typeof WidgetType)[keyof typeof WidgetType]

export type WidgetFieldConfig = {
  theme: boolean
  event: boolean
  date: boolean
  eventCount: boolean
  primaryColor: boolean
  checkoutMode?: boolean
}

export type WidgetDefinition = WidgetFieldConfig & {
  name: string
  widgetName: string
  showTooltip: boolean
  supportsModalPreview?: boolean
  type?: 'TABLE' | 'GUEST_LIST'
}

export const WIDGET_CONFIGS: Record<WidgetTypeValue, WidgetDefinition> = {
  [WidgetType.INVENTORY_MAP_WIDGET]: {
    name: 'Inventory Map',
    widgetName: 'inventory-list-map',
    showTooltip: false,
    theme: true,
    event: true,
    date: true,
    eventCount: false,
    primaryColor: true,
    checkoutMode: true,
  },
  [WidgetType.INVENTORY_LIST_WIDGET]: {
    name: 'Inventory List',
    widgetName: 'inventory-list',
    showTooltip: false,
    theme: true,
    event: true,
    date: true,
    eventCount: false,
    primaryColor: true,
    checkoutMode: true,
  },
  [WidgetType.CART_WIDGET]: {
    name: 'Cart Button',
    widgetName: 'cart-widget',
    showTooltip: false,
    supportsModalPreview: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
  },
  [WidgetType.RESERVATION_FORM_WIDGET]: {
    name: 'Reservation Form',
    widgetName: 'reservation-form',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
    type: 'TABLE',
  },
  [WidgetType.GUEST_LIST_FORM_WIDGET]: {
    name: 'Guest List Form',
    widgetName: 'guestlist-form',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
    type: 'GUEST_LIST',
  },
  [WidgetType.EVENT_CAROUSEL_WIDGET]: {
    name: 'Event Carousel',
    widgetName: 'event-carousel',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
    checkoutMode: true,
  },
  [WidgetType.EVENT_CALENDAR_WIDGET]: {
    name: 'Event Calendar',
    widgetName: 'event-calendar',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
    checkoutMode: true,
  },
  [WidgetType.EVENT_LIST_WIDGET]: {
    name: 'Event List',
    widgetName: 'event-list',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: true,
    checkoutMode: true,
  },
  [WidgetType.EVENT_GRID_WIDGET]: {
    name: 'Event Grid',
    widgetName: 'event-grid',
    showTooltip: false,
    theme: true,
    event: false,
    date: false,
    eventCount: false,
    primaryColor: false,
    checkoutMode: true,
  },
}

export const WIDGET_V2_TYPES = new Set<WidgetTypeValue>([
  WidgetType.EVENT_CAROUSEL_WIDGET,
  WidgetType.INVENTORY_MAP_WIDGET,
  WidgetType.INVENTORY_LIST_WIDGET,
  WidgetType.CART_WIDGET,
  WidgetType.RESERVATION_FORM_WIDGET,
  WidgetType.GUEST_LIST_FORM_WIDGET,
  WidgetType.EVENT_LIST_WIDGET,
  WidgetType.EVENT_CALENDAR_WIDGET,
  WidgetType.EVENT_GRID_WIDGET,
])

export const WIDGET_TYPE_ORDER: WidgetTypeValue[] = [
  WidgetType.INVENTORY_LIST_WIDGET,
  WidgetType.INVENTORY_MAP_WIDGET,
  WidgetType.CART_WIDGET,
  WidgetType.RESERVATION_FORM_WIDGET,
  WidgetType.GUEST_LIST_FORM_WIDGET,
  WidgetType.EVENT_LIST_WIDGET,
  WidgetType.EVENT_CALENDAR_WIDGET,
  WidgetType.EVENT_GRID_WIDGET,
  WidgetType.EVENT_CAROUSEL_WIDGET,
]

export const DEFAULT_WIDGET_TYPE = WidgetType.INVENTORY_LIST_WIDGET

export function isWidgetTypeValue(value: string): value is WidgetTypeValue {
  return Object.prototype.hasOwnProperty.call(WIDGET_CONFIGS, value)
}

export function resolveWidgetType(value: string | undefined): WidgetTypeValue {
  if (value && isWidgetTypeValue(value)) return value
  return DEFAULT_WIDGET_TYPE
}
